/**
 * Input Primitive
 * Form input component with validation states and design system tokens
 */

import React from "react";
import {
  Input as ChakraInput,
  FormControl,
  FormLabel,
  FormErrorMessage,
  FormHelperText,
  InputGroup,
  InputLeftElement,
  InputRightElement,
} from "@chakra-ui/react";
import {
  resolveSpacing,
} from "../foundation";
import { Color } from "../foundation/Color";
import { Spacing } from "../foundation/Spacing";
import { Radius } from "../foundation/Radius";
import { useColorModeValue } from "../hooks/useColorModeValue";

// Branded types for input tokens
declare const __input_size_brand: unique symbol;
declare const __input_state_brand: unique symbol;

export interface InputSizeToken {
  readonly [__input_size_brand]: true;
  readonly _value: string;
}

export interface InputStateToken {
  readonly [__input_state_brand]: true;
  readonly _value: string;
}

// Factory functions
const createInputSizeToken = (value: string): InputSizeToken => ({ _value: value } as InputSizeToken);
const createInputStateToken = (value: string): InputStateToken => ({ _value: value } as InputStateToken);

export interface InputProps {
  label?: string;
  helperText?: string;
  errorText?: string;
  state?: InputStateToken;
  size?: InputSizeToken;
  fullWidth?: boolean;
  leftIcon?: React.ReactNode;
  rightIcon?: React.ReactNode;
  leftElement?: React.ReactNode;
  rightElement?: React.ReactNode;
  placeholder?: string;
  value?: string;
  defaultValue?: string;
  onChange?: (event: React.ChangeEvent<HTMLInputElement>) => void;
  onBlur?: (event: React.FocusEvent<HTMLInputElement>) => void;
  onFocus?: (event: React.FocusEvent<HTMLInputElement>) => void;
  disabled?: boolean;
  readOnly?: boolean;
  required?: boolean;
  type?: string;
  name?: string;
  id?: string;
  "data-testid"?: string;
}

function InputComponent({
  label,
  helperText,
  errorText,
  state,
  size,
  fullWidth = false,
  leftIcon,
  rightIcon,
  leftElement,
  rightElement,
  placeholder,
  value,
  defaultValue,
  onChange,
  onBlur,
  onFocus,
  disabled = false,
  readOnly = false,
  required = false,
  type = "text",
  name,
  id,
  "data-testid": testId,
}: InputProps) {
  // Set defaults inside the component to avoid circular reference issues
  const resolvedState = state || inputTokens.state.default;
  const resolvedSize = size || inputTokens.size.medium;

  // Determine validation state
  const isInvalid = resolvedState._value === "error" || !!errorText;
  const isSuccess = resolvedState._value === "success";
  const isWarning = resolvedState._value === "warning";

  // Get state colors
  const getStateColors = () => {
    if (isInvalid) {
      return {
        borderColor: useColorModeValue(Color.primitive.red(500)),
        focusBorderColor: useColorModeValue(Color.primitive.red(500)),
        _focus: {
          borderColor: useColorModeValue(Color.primitive.red(500)),
          boxShadow: `0 0 0 1px ${useColorModeValue(Color.primitive.red(500))}`,
        },
      };
    } else if (isSuccess) {
      return {
        borderColor: useColorModeValue(Color.primitive.green(500)),
        focusBorderColor: useColorModeValue(Color.primitive.green(500)),
        _focus: {
          borderColor: useColorModeValue(Color.primitive.green(500)),
          boxShadow: `0 0 0 1px ${useColorModeValue(Color.primitive.green(500))}`,
        },
      };
    } else if (isWarning) {
      return {
        borderColor: useColorModeValue(Color.primitive.orange(500)),
        focusBorderColor: useColorModeValue(Color.primitive.orange(500)),
        _focus: {
          borderColor: useColorModeValue(Color.primitive.orange(500)),
          boxShadow: `0 0 0 1px ${useColorModeValue(Color.primitive.orange(500))}`,
        },
      };
    }

    return {
      borderColor: useColorModeValue(Color.border.primary),
      focusBorderColor: useColorModeValue(Color.interactive.primary),
      _focus: {
        borderColor: useColorModeValue(Color.interactive.primary),
        boxShadow: `0 0 0 1px ${useColorModeValue(Color.interactive.primary)}`,
      },
    };
  };

  const getSizeProps = (size: InputSizeToken) => {
    switch (size._value) {
      case "small":
        return {
          size: "sm" as const,
          fontSize: "sm",
          px: resolveSpacing(Spacing.small),
          py: resolveSpacing(Spacing.tiny),
        };
      case "large":
        return {
          size: "lg" as const,
          fontSize: "md",
          px: resolveSpacing(Spacing.medium),
          py: resolveSpacing(Spacing.small),
        };
      case "medium":
      default:
        return {
          size: "md" as const,
          fontSize: "sm",
          px: resolveSpacing(Spacing.small),
          py: resolveSpacing(Spacing.tiny),
        };
    }
  };

  const stateColors = getStateColors();
  const sizeProps = getSizeProps(resolvedSize);

  const InputElement = (
    <ChakraInput
      {...sizeProps}
      {...stateColors}
      bg={useColorModeValue(Color.surface.input)}
      color={useColorModeValue(Color.text.primary)}
      placeholder={placeholder}
      value={value}
      defaultValue={defaultValue}
      onChange={onChange}
      onBlur={onBlur}
      onFocus={onFocus}
      isDisabled={disabled}
      isReadOnly={readOnly}
      isRequired={required}
      type={type}
      name={name}
      id={id}
      data-testid={testId}
      borderRadius={Radius.input._value}
      width={fullWidth ? "full" : "auto"}
      _placeholder={{
        color: useColorModeValue(Color.text.placeholder),
      }}
      _disabled={{
        opacity: 0.6,
        cursor: "not-allowed",
        bg: useColorModeValue(Color.surface.secondary),
      }}
    />
  );

  return (
    <FormControl isInvalid={isInvalid} isRequired={required} width={fullWidth ? "full" : "auto"}>
      {label && (
        <FormLabel
          htmlFor={id}
          fontSize={sizeProps.fontSize}
          color={useColorModeValue(Color.text.primary)}
          mb={resolveSpacing(Spacing.tiny)}
        >
          {label}
        </FormLabel>
      )}

      {(leftIcon || rightIcon || leftElement || rightElement) ? (
        <InputGroup size={sizeProps.size}>
          {(leftIcon || leftElement) && (
            <InputLeftElement>
              {leftIcon || leftElement}
            </InputLeftElement>
          )}
          {InputElement}
          {(rightIcon || rightElement) && (
            <InputRightElement>
              {rightIcon || rightElement}
            </InputRightElement>
          )}
        </InputGroup>
      ) : (
        InputElement
      )}

      {errorText && (
        <FormErrorMessage
          fontSize="xs"
          color={useColorModeValue(Color.text.error)}
          mt={resolveSpacing(Spacing.tiny)}
        >
          {errorText}
        </FormErrorMessage>
      )}

      {helperText && !errorText && (
        <FormHelperText
          fontSize="xs"
          color={useColorModeValue(Color.text.secondary)}
          mt={resolveSpacing(Spacing.tiny)}
        >
          {helperText}
        </FormHelperText>
      )}
    </FormControl>
  );
}

// Input tokens
const inputTokens = {
  size: {
    small: createInputSizeToken("small"),
    medium: createInputSizeToken("medium"),
    large: createInputSizeToken("large"),
  } as const,

  state: {
    default: createInputStateToken("default"),
    success: createInputStateToken("success"),
    warning: createInputStateToken("warning"),
    error: createInputStateToken("error"),
  } as const,
};

// Public API - combines tokens and component
export const Input = Object.assign(InputComponent, inputTokens);
